import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Text, Environment, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';


// Enhanced Dialogue bubble component
function DialogueBubble({ text, position, isTeacher = false }) {
  const bubbleRef = useRef();

  useFrame((state) => {
    if (bubbleRef.current) {
      bubbleRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.05;
      bubbleRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.02;
    }
  });

  return (
    <group ref={bubbleRef} position={position}>
      {/* Bubble background - more detailed */}
      <mesh>
        <sphereGeometry args={[0.9, 20, 20]} />
        <meshStandardMaterial 
          color={isTeacher ? "#2E5BBA" : "#C2185B"} 
          transparent 
          opacity={0.85}
          emissive={isTeacher ? "#2E5BBA" : "#C2185B"}
          emissiveIntensity={0.1}
        />
      </mesh>
      
      {/* Bubble border */}
      <mesh>
        <sphereGeometry args={[0.95, 20, 20]} />
        <meshStandardMaterial 
          color={isTeacher ? "#1A4A8A" : "#8E0038"} 
          transparent 
          opacity={0.3}
          wireframe={true}
        />
      </mesh>
      
      {/* Bubble tail */}
      <mesh position={[0, -0.7, 0]} rotation={[Math.PI / 4, 0, 0]}>
        <coneGeometry args={[0.2, 0.4, 8]} />
        <meshStandardMaterial 
          color={isTeacher ? "#2E5BBA" : "#C2185B"} 
          transparent 
          opacity={0.85}
        />
      </mesh>
      
      {/* Text with better styling */}
      <Text
        position={[0, 0, 0.15]}
        fontSize={0.18}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        maxWidth={1.6}
        font="/fonts/inter.woff"
        strokeWidth={0.02}
        strokeColor="#000000"
      >
        {text}
      </Text>
      
      {/* Speaking indicator dots */}
      <mesh position={[-0.3, -0.2, 0.1]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0, -0.2, 0.1]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.3, -0.2, 0.1]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
    </group>
  );
}

// Classroom environment
function Classroom() {
  return (
    <>
      {/* Floor */}
      <mesh position={[0, -2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, 20]} />
        <meshStandardMaterial color="#8B4513" />
      </mesh>
      
      {/* Walls */}
      <mesh position={[0, 2, -10]} rotation={[0, 0, 0]}>
        <planeGeometry args={[20, 8]} />
        <meshStandardMaterial color="#F5F5DC" />
      </mesh>
      
      {/* Blackboard */}
      <mesh position={[0, 1, -9.9]} rotation={[0, 0, 0]}>
        <planeGeometry args={[6, 3]} />
        <meshStandardMaterial color="#2C3E50" />
      </mesh>
      
      {/* Chalk text on blackboard - removed */}
      
      {/* Desks */}
      {Array.from({ length: 6 }, (_, i) => (
        <mesh key={i} position={[-4 + (i % 3) * 4, -1.5, -2 + Math.floor(i / 3) * 2]}>
          <boxGeometry args={[1, 0.1, 0.5]} />
          <meshStandardMaterial color="#8B4513" />
        </mesh>
      ))}
    </>
  );
}

// Main dialogues scene
function DialoguesScene3D({ dialoguesContent, currentDialogue = 0 }) {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 3, 8);
    camera.lookAt(0, 0, -2);
  }, [camera]);

  const isTeacherSpeaking = currentDialogue % 2 === 0;
  const isStudentSpeaking = !isTeacherSpeaking;

  return (
    <>
      {/* Basic lighting */}
      <ambientLight intensity={0.8} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      
      {/* Teacher - simple blue sphere */}
      <mesh position={[0, 0, -5]}>
        <sphereGeometry args={[1, 16, 16]} />
        <meshStandardMaterial color="#4A90E2" />
      </mesh>
      
      {/* Students - simple colored spheres */}
      <mesh position={[-3, 0, -2]}>
        <sphereGeometry args={[1, 16, 16]} />
        <meshStandardMaterial color="#E24A90" />
      </mesh>
      <mesh position={[0, 0, -2]}>
        <sphereGeometry args={[1, 16, 16]} />
        <meshStandardMaterial color="#90E24A" />
      </mesh>
      <mesh position={[3, 0, -2]}>
        <sphereGeometry args={[1, 16, 16]} />
        <meshStandardMaterial color="#E2E24A" />
      </mesh>
      
      
      {/* Dialogue bubbles */}
      {isTeacherSpeaking && (
        <DialogueBubble 
          text="Let's explore this concept together..." 
          position={[0, 2, -3]} 
          isTeacher={true}
        />
      )}
      
      {isStudentSpeaking && (
        <DialogueBubble 
          text="I have a question about..." 
          position={[-3, 1.5, -1]} 
          isTeacher={false}
        />
      )}
    </>
  );
}

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('3D Scene Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div>
            <h3>3D Scene Loading...</h3>
            <p>Please wait while the 3D environment initializes.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main component
const DialoguesScene = ({ dialoguesContent, currentDialogue = 0 }) => {
  
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <ErrorBoundary>
        <Canvas
          camera={{ position: [0, 3, 8], fov: 60 }}
          style={{ background: 'transparent' }}
          onCreated={({ gl, scene, camera }) => {
            gl.setClearColor(0x000000, 0);
          }}
        >
          <DialoguesScene3D 
            dialoguesContent={dialoguesContent} 
            currentDialogue={currentDialogue}
          />
          <OrbitControls
            enablePan={false}
            enableZoom={true}
            enableRotate={true}
            minDistance={5}
            maxDistance={15}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </ErrorBoundary>
    </div>
  );
};

export default DialoguesScene;


